import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import ByzMuiTypography from "src/components/core-components/mui/inputs/ByzMuiTypography";
import ByzMuiButton from "src/components/core-components/mui/buttons/ByzMuiButton";
import ByzMuiDeleteButton from "src/components/core-components/mui/buttons/ByzMuiDeleteButton";
import { ByzMuiIconButton } from "src/components/core-components/mui/buttons/ByzMuiIconButton";
import PatientCareGuide from "./patient-care-guide";
import PatientCareEdit from "./modal/patient-care-edit";
import PatientCareNurseEdit from "./modal/patient-care-nurse-edit";
import PatientCarePlanEdit from "./modal/patient-care-plan-edit";
import PatientCareNursePlanAdd from "./modal/patient-care-nurse-plan-add";
import { useTranslate } from "src/locales";
import { DIALOG_PAPER_PROPS, ACTION_DIALOG_PAPER_PROPS } from "./constants";
import { DeleteState, SecondGridDeleteState } from "./models";

interface PatientCareDialogsProps {
  patientId: number;
  patientInfo: any;
  dialogOpen: boolean;
  onCloseDialog: () => void;
  isEditDialogOpen: boolean;
  currentEditRow: any;
  onCloseDialogEdit: () => void;
  onSaveEdit: (row: any) => void;
  isNurseEditDialogOpen: boolean;
  nurseCurrentEditRow: any;
  onCloseDialogNurseEdit: () => void;
  onSaveNurseEdit: (row: any) => void;
  isPlanEditDialogOpen: boolean;
  planCurrentEditRow: any;
  onCloseDialogPlanEdit: () => void;
  onSavePlanEdit: (row: any) => void;
  dialogTreeOpen: boolean;
  setDialogTreeOpen: (open: boolean) => void;
  selectedTreeId: number | null;
  dialogTreeNewOpen: boolean;
  onCloseTreeNewDialog: () => void;
  selectedRowDataForNewPeriod: any;
  setDialogTreeNewOpen: (open: boolean) => void;
  onSuccessNewPlan: () => void;
  deleteState: DeleteState;
  setDeleteState: (state: DeleteState) => void;
  onDeleteConfirm: () => void;
  isDeleting: boolean;
  secondGridDeleteState: SecondGridDeleteState;
  setSecondGridDeleteState: (state: SecondGridDeleteState) => void;
  onSecondGridDeleteConfirm: () => void;
  isSecondGridDeleting: boolean;
}

const PatientCareDialogs = ({
  patientId, patientInfo, dialogOpen, onCloseDialog,
  isEditDialogOpen, currentEditRow, onCloseDialogEdit, onSaveEdit,
  isNurseEditDialogOpen, nurseCurrentEditRow, onCloseDialogNurseEdit, onSaveNurseEdit,
  isPlanEditDialogOpen, planCurrentEditRow, onCloseDialogPlanEdit, onSavePlanEdit,
  dialogTreeOpen, setDialogTreeOpen, selectedTreeId,
  dialogTreeNewOpen, onCloseTreeNewDialog, selectedRowDataForNewPeriod, setDialogTreeNewOpen, onSuccessNewPlan,
  deleteState, setDeleteState, onDeleteConfirm, isDeleting,
  secondGridDeleteState, setSecondGridDeleteState, onSecondGridDeleteConfirm, isSecondGridDeleting
}: PatientCareDialogsProps) => {
  const { t } = useTranslate();

  return (
    <>
      <OpenComponentDialog
        open={dialogOpen}
        onClose={onCloseDialog}
        hideHeader={true}
        showPatientData={false}
        isMedCard={false}
      >
        <PatientCareGuide key={patientId} patientId={patientId} patientInfo={patientInfo} onClose={onCloseDialog} />
      </OpenComponentDialog>

      <OpenComponentDialog
        title={t("patient-care.planning-added-patient")}
        open={isEditDialogOpen}
        onClose={onCloseDialogEdit}
        isMedCard={false}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <PatientCareEdit
          selectedRow={currentEditRow}
          pcsid={currentEditRow?.id}
          onClose={onCloseDialogEdit}
          onSave={onSaveEdit}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        title={t("patient-care.nursing-intervention")}
        open={isNurseEditDialogOpen}
        onClose={onCloseDialogNurseEdit}
        isMedCard={false}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <PatientCareNurseEdit
          selectedRow={nurseCurrentEditRow}
          pcsid={nurseCurrentEditRow?.id}
          onClose={onCloseDialogNurseEdit}
          onSave={onSaveNurseEdit}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        title={t("patient-care.planing-seans")}
        open={isPlanEditDialogOpen}
        onClose={onCloseDialogPlanEdit}
        isMedCard={false}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <PatientCarePlanEdit
          selectedRow={planCurrentEditRow}
          pcsid={planCurrentEditRow?.id}
          onClose={onCloseDialogPlanEdit}
          onSave={onSavePlanEdit}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        open={dialogTreeOpen}
        onClose={() => setDialogTreeOpen(false)}
        isMedCard={false}
        PaperProps={ACTION_DIALOG_PAPER_PROPS}
      >
        <PatientCareNursePlanAdd
          pcsid={selectedTreeId}
          setDialogTreeOpen={setDialogTreeOpen}
        />
      </OpenComponentDialog>

      <Dialog
        open={dialogTreeNewOpen}
        onClose={onCloseTreeNewDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={ACTION_DIALOG_PAPER_PROPS}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "0.9rem", sm: "1rem" },
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {t("patient-care.new-period-planning")}
          <ByzMuiIconButton onClick={onCloseTreeNewDialog}>
            <CloseIcon />
          </ByzMuiIconButton>
        </DialogTitle>
        <DialogContent>
          <PatientCareNursePlanAdd
            pcsid={selectedRowDataForNewPeriod?.id}
            setDialogTreeOpen={setDialogTreeNewOpen}
            newPlan={true}
            selectedRowData={selectedRowDataForNewPeriod}
            selectedPlan={selectedRowDataForNewPeriod?.nursingInitiative}
            currentPeriod={selectedRowDataForNewPeriod?.period}
            onSuccess={onSuccessNewPlan}
          />
        </DialogContent>
      </Dialog>

      {deleteState.open && (
        <Dialog
          open={deleteState.open}
          onClose={() => setDeleteState({ ...deleteState, open: false })}
          fullWidth
          sx={{ borderRadius: 3 }}
        >
          <DialogTitle
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {deleteState.isGuide ? t("patient-care.delete-selected-guide") : t("patient-care.delete-selected-plan")}
            <ByzMuiIconButton onClick={() => setDeleteState({ ...deleteState, open: false })}>
              <CloseIcon />
            </ByzMuiIconButton>
          </DialogTitle>
          <DialogContent>
            <ByzMuiTypography>
              {deleteState.isGuide ? t("patient-care.delete-guide-content") : t("patient-care.delete-selected-plan")}
            </ByzMuiTypography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <ByzMuiButton onClick={() => setDeleteState({ ...deleteState, open: false })} sx={{ mr: 1 }}>
              {t("buttons.cancel")}
            </ByzMuiButton>
            <ByzMuiDeleteButton onClick={onDeleteConfirm} loading={isDeleting} />
          </DialogActions>
        </Dialog>
      )}

      {secondGridDeleteState.open && (
        <Dialog
          open={secondGridDeleteState.open}
          onClose={() => setSecondGridDeleteState({ ...secondGridDeleteState, open: false })}
          fullWidth
          sx={{ borderRadius: 3 }}
        >
          <DialogTitle
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "0.9rem", sm: "1rem" },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            {secondGridDeleteState.deleteType === 'intervention' 
              ? t("patient-care.delete-selected-intervention") 
              : t("patient-care.delete-planning")}
            <ByzMuiIconButton onClick={() => setSecondGridDeleteState({ ...secondGridDeleteState, open: false })}>
              <CloseIcon />
            </ByzMuiIconButton>
          </DialogTitle>
          <DialogContent>
            <ByzMuiTypography>
              {secondGridDeleteState.deleteType === 'intervention' 
                ? t("patient-care.delete-intervention-content") 
                : t("patient-care.delete-planning-content")}
            </ByzMuiTypography>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <ByzMuiButton onClick={() => setSecondGridDeleteState({ ...secondGridDeleteState, open: false })} sx={{ mr: 1 }}>
              {t("buttons.cancel")}
            </ByzMuiButton>
            <ByzMuiDeleteButton onClick={onSecondGridDeleteConfirm} loading={isSecondGridDeleting} />
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default PatientCareDialogs;
